import { create } from 'zustand'
import { persist } from 'zustand/middleware'

// --- Global flag to enable/disable console logs for this store ---
const ENABLE_EMAIL_CAPTURE_LOGS = false; // Set to false to disable logs
// -------------------------------------------------------------------

export interface UserEngagement {
    timeOnSite: number // Total time spent on site (ms)
    scrollDepth: number // Highest scroll percentage achieved
    pageViews: number // Pages viewed in current session
    productPageViews: number // Product pages viewed
    returnVisits: number // How many times they've visited
    lastVisit: number // Timestamp of last visit
    hasAddedToCart: boolean // Whether they've added items to cart
    sessionStart: number // When current session started
}

export interface EmailCaptureState {
    isModalOpen: boolean
    hasSubscribed: boolean
    hasDismissed: boolean
    dismissCount: number // How many times dismissed
    lastDismissed: number // When last dismissed
    modalType: 'newsletter' | 'discount' // Type of modal to show
    optimalTiming: {
        shouldShow: boolean
        trigger: string // What triggered the modal
        confidence: number // 0-1 confidence score
    }
}

interface EmailCaptureStore {
    engagement: UserEngagement
    modalState: EmailCaptureState
    cookiesAccepted: boolean

    // Actions
    updateEngagement: (updates: Partial<UserEngagement>) => void
    incrementPageView: (isProductPage?: boolean) => void
    updateScrollDepth: (depth: number) => void
    markAddedToCart: () => void
    clearCartState: () => void
    setCookiesAccepted: (accepted: boolean) => void

    // Modal actions
    showModal: (trigger: string, confidence: number, modalType?: 'newsletter' | 'discount') => void
    showDiscountModal: (trigger: string, confidence: number) => void
    hideModal: () => void
    markSubscribed: (email: string) => void
    markDismissed: () => void

    // Timing logic
    checkOptimalTiming: () => void
    resetSession: () => void
    resetStoreForDebug: () => void
}

const initialEngagement: UserEngagement = {
    timeOnSite: 0,
    scrollDepth: 0,
    pageViews: 0,
    productPageViews: 0,
    returnVisits: 1,
    lastVisit: Date.now(),
    hasAddedToCart: false,
    sessionStart: Date.now(),
}

const initialModalState: EmailCaptureState = {
    isModalOpen: false,
    hasSubscribed: false,
    hasDismissed: false,
    dismissCount: 0,
    lastDismissed: 0,
    modalType: 'newsletter',
    optimalTiming: {
        shouldShow: false,
        trigger: '',
        confidence: 0,
    }
}

const useEmailCaptureStore = create<EmailCaptureStore>()(
    persist(
        (set, get) => ({
            engagement: initialEngagement,
            modalState: initialModalState,
            cookiesAccepted: false,

            updateEngagement: (updates) =>
                set((state) => ({
                    engagement: { ...state.engagement, ...updates }
                })),

            incrementPageView: (isProductPage = false) =>
                set((state) => ({
                    engagement: {
                        ...state.engagement,
                        pageViews: state.engagement.pageViews + 1,
                        productPageViews: isProductPage
                            ? state.engagement.productPageViews + 1
                            : state.engagement.productPageViews
                    }
                })),

            updateScrollDepth: (depth) =>
                set((state) => ({
                    engagement: {
                        ...state.engagement,
                        scrollDepth: Math.max(state.engagement.scrollDepth, depth)
                    }
                })),

            markAddedToCart: () =>
                set((state) => ({
                    engagement: { ...state.engagement, hasAddedToCart: true }
                })),

            clearCartState: () =>
                set((state) => ({
                    engagement: { ...state.engagement, hasAddedToCart: false }
                })),

            setCookiesAccepted: (accepted) =>
                set(() => ({ cookiesAccepted: accepted })),

            showModal: (trigger, confidence, modalType = 'newsletter') =>
                set((state) => ({
                    modalState: {
                        ...state.modalState,
                        isModalOpen: true,
                        optimalTiming: { shouldShow: true, trigger, confidence },
                        modalType
                    }
                })),

            showDiscountModal: (trigger, confidence) =>
                set((state) => ({
                    modalState: {
                        ...state.modalState,
                        isModalOpen: true,
                        optimalTiming: { shouldShow: true, trigger, confidence },
                        modalType: 'discount'
                    }
                })),

            hideModal: () =>
                set((state) => ({
                    modalState: { ...state.modalState, isModalOpen: false }
                })),

            markSubscribed: (email) =>
                set((state) => ({
                    modalState: {
                        ...state.modalState,
                        hasSubscribed: true,
                        isModalOpen: false
                    }
                })),

            markDismissed: () =>
                set((state) => ({
                    modalState: {
                        ...state.modalState,
                        hasDismissed: true,
                        isModalOpen: false,
                        dismissCount: state.modalState.dismissCount + 1,
                        lastDismissed: Date.now()
                    }
                })),

            checkOptimalTiming: () => {
                const state = get()
                const { engagement, modalState } = state

                if (ENABLE_EMAIL_CAPTURE_LOGS) console.log('[EmailCapture] Checking optimal timing...', {
                    hasSubscribed: modalState.hasSubscribed,
                    lastDismissed: modalState.lastDismissed,
                    timeSinceLastDismiss: modalState.lastDismissed > 0 ? Date.now() - modalState.lastDismissed : 'never',
                    isModalOpen: modalState.isModalOpen,
                    hasAddedToCart: engagement.hasAddedToCart
                })

                // Don't show if already subscribed or dismissed recently
                if (modalState.hasSubscribed) {
                    if (ENABLE_EMAIL_CAPTURE_LOGS) console.log('[EmailCapture] Already subscribed, skipping')
                    return
                }
                if (modalState.lastDismissed > 0 &&
                    Date.now() - modalState.lastDismissed < 24 * 60 * 60 * 1000) {
                    if (ENABLE_EMAIL_CAPTURE_LOGS) console.log('[EmailCapture] Dismissed recently, skipping')
                    return
                }

                // PRIORITY: Don't interrupt purchase flow - skip if user has added to cart
                if (engagement.hasAddedToCart) {
                    if (ENABLE_EMAIL_CAPTURE_LOGS) console.log('[EmailCapture] User has items in cart - prioritizing sales flow, skipping modal')
                    return
                }

                const timeOnSite = Date.now() - engagement.sessionStart
                let score = 0
                let trigger = ''
                let modalType: 'newsletter' | 'discount' = 'discount' // Default to discount

                // Safety checks for NaN values
                const safeScrollDepth = isNaN(engagement.scrollDepth) ? 0 : engagement.scrollDepth
                const safePageViews = isNaN(engagement.pageViews) ? 0 : engagement.pageViews
                const safeProductPageViews = isNaN(engagement.productPageViews) ? 0 : engagement.productPageViews

                if (ENABLE_EMAIL_CAPTURE_LOGS) console.log('[EmailCapture] Current engagement:', {
                    timeOnSite: timeOnSite / 1000 + 's',
                    scrollDepth: safeScrollDepth + '%',
                    pageViews: safePageViews,
                    productPageViews: safeProductPageViews,
                    returnVisits: engagement.returnVisits,
                    hasAddedToCart: engagement.hasAddedToCart
                })

                // Reduce score based on dismissal history (early check for logging clarity)
                if (modalState.dismissCount > 0) {
                    const reduction = modalState.dismissCount * 0.20;
                    if (ENABLE_EMAIL_CAPTURE_LOGS) console.log(`[EmailCapture] Initial check: ${modalState.dismissCount} dismissals will reduce score by ${reduction}`);
                }

                // Base engagement scoring
                if (timeOnSite > 15000) score += 0.15; // 15s
                if (timeOnSite > 30000) score += 0.15; // 30s
                if (timeOnSite > 60000) score += 0.1;  // 1min
                if (safeScrollDepth > 25) score += 0.1;
                if (safeScrollDepth > 50) score += 0.15;
                if (safePageViews >= 2) score += 0.1;
                if (safePageViews >= 3) score += 0.1;
                if (safeProductPageViews >= 1) score += 0.2;

                if (ENABLE_EMAIL_CAPTURE_LOGS) console.log('[EmailCapture] Base score calculated:', score);

                // High-intent triggers (prioritize discount)
                if (safeProductPageViews >= 1 && timeOnSite > 10000) {
                    score += 0.4; // Strong signal for discount
                    trigger = 'product-view-10s-discount';
                    modalType = 'discount';
                    if (ENABLE_EMAIL_CAPTURE_LOGS) console.log('[EmailCapture] Trigger condition met: product-view-10s-discount. Score +0.4. Modal type: discount');
                }
                else if (safeProductPageViews >= 1 && timeOnSite > 35000) { // Viewed a product & spent some time (was 45000, was 2 product views)
                    score += 0.35;
                    trigger = 'multi-product-interest'; // Keeping original trigger name, though behavior changed
                    modalType = 'discount';
                    if (ENABLE_EMAIL_CAPTURE_LOGS) console.log('[EmailCapture] Trigger condition met: multi-product-interest (now 1+ product view & >35s). Score +0.35. Modal type: discount');
                } else if (timeOnSite > 90000 && safeScrollDepth > 70) { // Very deep engagement
                    score += 0.4;
                    trigger = 'very-deep-engagement';
                    modalType = 'discount';
                    if (ENABLE_EMAIL_CAPTURE_LOGS) console.log('[EmailCapture] Trigger condition met: very-deep-engagement. Score +0.4. Modal type: discount');
                } else if (engagement.returnVisits >= 3 && safePageViews >= 2) { // Frequent return visitor, active
                    score += 0.35;
                    trigger = 'frequent-return-visitor';
                    modalType = 'discount';
                    if (ENABLE_EMAIL_CAPTURE_LOGS) console.log('[EmailCapture] Trigger condition met: frequent-return-visitor. Score +0.35. Modal type: discount');
                }
                // Mid-intent triggers (can be newsletter or discount based on other factors)
                else if (timeOnSite > 50000 && safePageViews >= 3) { // Good exploration (was 60000)
                    score += 0.3;
                    trigger = 'extended-site-exploration';
                    modalType = 'discount'; // Still likely good for a discount
                    if (ENABLE_EMAIL_CAPTURE_LOGS) console.log('[EmailCapture] Trigger condition met: extended-site-exploration. Score +0.3. Modal type: discount');
                } else if (safeProductPageViews >= 1 && timeOnSite > 25000) { // Viewed product, spent some time (was 30000)
                    score += 0.25;
                    trigger = 'product-page-dwell';
                    modalType = 'discount';
                    if (ENABLE_EMAIL_CAPTURE_LOGS) console.log('[EmailCapture] Trigger condition met: product-page-dwell. Score +0.25. Modal type: discount');
                }
                // Lower-intent / general engagement (newsletter focus)
                else if (engagement.returnVisits === 2 && timeOnSite > 20000) { // Second visit, some engagement
                    score += 0.2;
                    trigger = 'return-visitor-engaged';
                    modalType = 'newsletter';
                    if (ENABLE_EMAIL_CAPTURE_LOGS) console.log('[EmailCapture] Trigger condition met: return-visitor-engaged. Score +0.2. Modal type: newsletter');
                } else if (timeOnSite > 45000 && safeScrollDepth > 50) { // Decent time and scroll
                    score += 0.25;
                    trigger = 'content-focused-visitor';
                    modalType = 'newsletter';
                    if (ENABLE_EMAIL_CAPTURE_LOGS) console.log('[EmailCapture] Trigger condition met: content-focused-visitor. Score +0.25. Modal type: newsletter');
                }


                // Fallback trigger for general engagement - requires a higher score now
                // and defaults to newsletter unless other high-intent signals were present
                if (!trigger && score >= 0.4) { // Increased threshold for general engagement
                    trigger = 'generally-engaged-visitor';
                    modalType = (modalType === 'discount' && score > 0.5) ? 'discount' : 'newsletter'; // Keep discount if score is quite high
                    if (ENABLE_EMAIL_CAPTURE_LOGS) console.log(`[EmailCapture] Setting fallback trigger for generally engaged visitor (score: ${score}), modalType: ${modalType}`);
                }

                // Reduce score based on dismissal history
                if (modalState.dismissCount > 0) {
                    const reduction = modalState.dismissCount * 0.20; // Slightly increased penalty for dismissals
                    score -= reduction;
                    if (ENABLE_EMAIL_CAPTURE_LOGS) console.log(`[EmailCapture] -${reduction} for ${modalState.dismissCount} dismissals. New score: ${score}`);
                }

                if (ENABLE_EMAIL_CAPTURE_LOGS) console.log('[EmailCapture] Final evaluation:', {
                    score,
                    trigger,
                    modalType,
                    configuredThreshold: 0.35 // Adjusted overall threshold
                });

                // Adjusted overall threshold for showing modal
                const showThreshold = 0.35;

                if (score >= showThreshold && trigger && !modalState.isModalOpen) {
                    if (ENABLE_EMAIL_CAPTURE_LOGS) console.log('[EmailCapture] 🎯 Showing modal!', { trigger, finalScore: score, modalType, threshold: showThreshold });
                    state.showModal(trigger, score, modalType);
                } else {
                    if (ENABLE_EMAIL_CAPTURE_LOGS) console.log('[EmailCapture] Not showing modal. Conditions:', {
                        scoreMetThreshold: score >= showThreshold,
                        triggerPresent: !!trigger,
                        modalCurrentlyOpen: modalState.isModalOpen,
                        finalScore: score,
                        triggerValue: trigger,
                        requiredThreshold: showThreshold
                    });
                }
            },

            resetSession: () =>
                set((state) => ({
                    engagement: {
                        ...state.engagement,
                        timeOnSite: 0,
                        scrollDepth: 0,
                        pageViews: 0,
                        productPageViews: 0,
                        sessionStart: Date.now(),
                        returnVisits: state.engagement.returnVisits + 1,
                        lastVisit: Date.now(),
                    }
                })),

            resetStoreForDebug: () =>
                set(() => ({
                    engagement: {
                        ...initialEngagement, // Use defaults from initialEngagement
                        timeOnSite: 0, // Explicitly reset calculated/accumulated time
                        sessionStart: Date.now(), // Set to current time
                        lastVisit: Date.now(),    // Set to current time
                        // pageViews, productPageViews, scrollDepth are 0 in initialEngagement, so they are fine
                        // returnVisits is 1 in initialEngagement, which is correct for a "fresh" session start
                        // hasAddedToCart is false in initialEngagement, also correct.
                    },
                    modalState: initialModalState,
                })),

            // Add onRehydrateStorage to clean up any NaN values and handle session reset
            onRehydrateStorage: () => (state?: EmailCaptureStore, error?: Error | unknown) => {
                if (error) {
                    console.error("[EmailCapture] Error rehydrating store:", error);
                    // Potentially reset to initial state or handle error appropriately
                    if (state) {
                        state.engagement = { ...initialEngagement, sessionStart: Date.now(), lastVisit: Date.now() };
                        state.modalState = { ...initialModalState };
                    }
                    return;
                }
                if (state?.engagement) {
                    if (ENABLE_EMAIL_CAPTURE_LOGS) console.log("[EmailCapture] Store rehydrated. Last visit was:", new Date(state.engagement.lastVisit).toLocaleString());
                    // Clean up any NaN values that might have been persisted
                    if (isNaN(state.engagement.scrollDepth)) state.engagement.scrollDepth = 0;
                    if (isNaN(state.engagement.pageViews)) state.engagement.pageViews = 0;
                    if (isNaN(state.engagement.productPageViews)) state.engagement.productPageViews = 0;
                    if (isNaN(state.engagement.timeOnSite)) state.engagement.timeOnSite = 0;
                    if (isNaN(state.engagement.returnVisits)) state.engagement.returnVisits = 1; // Default to 1 if NaN
                    if (isNaN(state.engagement.lastVisit)) state.engagement.lastVisit = Date.now(); // Default to now if NaN
                    if (isNaN(state.engagement.sessionStart)) state.engagement.sessionStart = Date.now(); // Default to now if NaN

                    const thirtyMinutes = 30 * 60 * 1000;
                    const timeSinceLastVisit = Date.now() - state.engagement.lastVisit;

                    if (ENABLE_EMAIL_CAPTURE_LOGS) console.log(`[EmailCapture] Time since last visit: ${Math.round(timeSinceLastVisit / 60000)} minutes.`);

                    if (timeSinceLastVisit > thirtyMinutes) {
                        if (ENABLE_EMAIL_CAPTURE_LOGS) console.log("[EmailCapture] New session detected (last visit > 30 mins ago). Calling resetSession.");
                        // This is a new session, call resetSession logic directly
                        // resetSession increments returnVisits and resets session-specific data
                        state.engagement.returnVisits = (state.engagement.returnVisits || 0) + 1;
                        state.engagement.sessionStart = Date.now();
                        state.engagement.lastVisit = Date.now(); // Update lastVisit for the new session
                        state.engagement.pageViews = 0;
                        state.engagement.productPageViews = 0;
                        state.engagement.scrollDepth = 0;
                        state.engagement.timeOnSite = 0; // Reset timeOnSite for the new session
                        state.engagement.hasAddedToCart = false; // Reset cart status for new session
                        if (ENABLE_EMAIL_CAPTURE_LOGS) console.log("[EmailCapture] New session state after resetSession logic:", state.engagement);
                    } else {
                        if (ENABLE_EMAIL_CAPTURE_LOGS) console.log("[EmailCapture] Continuing existing session (last visit < 30 mins ago).");
                        // It's a continuation of the same session, just update sessionStart to now
                        // and ensure lastVisit is also updated for the next check.
                        state.engagement.sessionStart = Date.now();
                        state.engagement.lastVisit = Date.now(); // Update last visit time for current activity
                    }
                } else if (state) {
                    // If engagement is missing, initialize it (e.g. first time load after this logic is deployed)
                    if (ENABLE_EMAIL_CAPTURE_LOGS) console.log("[EmailCapture] No engagement state found on rehydration, initializing.");
                    state.engagement = { ...initialEngagement, sessionStart: Date.now(), lastVisit: Date.now(), returnVisits: 1 };
                    state.modalState = { ...initialModalState }; // Ensure modal state is also initialized
                }
            }
        }),
        {
            name: 'email-capture-storage',
            partialize: (state) => ({
                // Always persist modal state (essential for user experience)
                modalState: {
                    hasSubscribed: state.modalState.hasSubscribed,
                    hasDismissed: state.modalState.hasDismissed,
                    dismissCount: state.modalState.dismissCount,
                    lastDismissed: state.modalState.lastDismissed,
                },
                // Only persist detailed engagement if cookies accepted, otherwise minimal data
                engagement: state.cookiesAccepted ? state.engagement : {
                    returnVisits: state.engagement.returnVisits,
                    lastVisit: state.engagement.lastVisit,
                    sessionStart: state.engagement.sessionStart, // Always need this for current session
                },
                // Don't persist cookiesAccepted flag itself
            }),
        }
    )
)

export default useEmailCaptureStore 